import React from "react"
import { graphql, useStaticQuery, Link } from "gatsby"
import tw, { styled } from "twin.macro"
import { StaticImage } from "gatsby-plugin-image"

import GlobeVideo from "images/bg/globe-data-points.mp4"
import Demand360Logo from "components/demand360-logo"
import Demand360Features from "components/sections/demand360-features"
import Demand360AvatarCards from "components/sections/demand360-avatar-cards"
import ContactUsBg from "images/bg/industry-bw.jpeg"
import StyledBlock from "components/newsroom/styled-block"

const Styles = styled.div`
  ${tw`flex flex-col relative`}

  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000000 17.09%, #000000 51.88%, rgba(0, 0, 0, 0) 100%);

  @media (min-width: 640px) {
    min-height: 780px;
  }

  @media (min-width: 1200px) {
    min-height: 1045px;
  }

  &.home {
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 0%,
      #000000 14.51%,
      #000000 61.61%,
      rgba(0, 0, 0, 0) 99.68%
    );
  }

  &.demand360 {
    .media {
      &.points {
        ${tw`l:-mt-10`}

        @media (min-width: 1200px) {
          .hero {
            top: 270px;
          }
        }
      }
    }
  }

  &.contact-us {
    background: linear-gradient(
        180deg,
        #000000 68.05%,
        rgba(45, 0, 112, 0.32) 100%
      ),
      url(${ContactUsBg});
  }

  .text {
    ${tw`flex flex-col items-center text-center mx-auto max-w-s m:max-w-m l:max-w-l relative`}

    z-index: 4;

    .parrot-logo {
      ${tw`mb-4 l:mb-6`}

      width: 70px;

      @media (min-width: 640px) {
        width: 90px;
      }
    }

    .content {
      ${tw`mt-10 l:mt-6 text-white`}

      h4 {
        ${tw`font-extrabold`}

        font-size: 27px;
        line-height: 37px;

        @media (min-width: 640px) {
          ${tw`font-bold`}

          line-height: 40px;
        }
      }
    }
  }

  .media {
    ${tw`flex flex-col items-center text-center relative mx-auto m:max-w-m l:max-w-l w-full`}

    padding-bottom: 23px;
    margin-top: -30px;

    @media (min-width: 640px) {
      padding-bottom: 235px;
      margin-top: -150px;
    }

    .hero {
      ${tw`w-full absolute`}

      z-index: 3;
      top: 123px;

      @media (min-width: 640px) {
        top: 323px;
      }

      @media (min-width: 1200px) {
        width: 70%;
      }
    }

    .data-points {
      ${tw`hidden`}
    }

    .globe {
      ${tw`w-full`}

      z-index: 1;
      aspect-ratio: 16 / 9;

      @media (min-width: 720px) {
        ${tw`w-auto`}

        height: 405px;
      }

      @media (min-width: 1200px) {
        height: 675px;
      }
    }

    &.points {
      ${tw`-mt-8 m:mt-0`}

      padding-bottom: 85px;

      .hero {
        top: 115px;
      }

      @media (min-width: 640px) {
        padding-bottom: 0px;

        .hero {
          top: 200px;
          width: 70%;
        }

        .data-points {
          ${tw`block absolute`}

          top: 0px;
          z-index: 2;
        }
      }

      @media (min-width: 1200px) {
        .hero {
          top: 320px;
        }
      }
    }
  }

  .actions {
    ${tw`flex flex-col items-center text-center relative mx-auto m:max-w-m l:max-w-l relative l:-mt-12`}

    z-index: 4;

    .content {
      ${tw`hidden l:block text-grey-65 l:w-4/5 mt-16`}

      font-size: 22px;
      line-height: 33px;
    }

    .buttons {
      ${tw`flex flex-col-reverse space-y-reverse space-y-6 l:space-y-0 space-x-0 l:space-x-4 l:flex-row`}

      & > div {
        /* For some reason the 'space-y-reverse' class above does not work */
        --space-y-reverse: 1 !important;
      }

      button {
        ${tw`w-full`}

        @media (min-width: 640px) {
          width: 270px;
        }
      }

      .flat {
        button {
          ${tw`text-neonred`}
        }
      }
    }
  }

  ${StyledBlock} {
    &.secondary-section-header {
      ${tw`mx-auto`}

      max-width: 90%;

      @media (min-width: 1200px) {
        max-width: 900px;
      }
    }
  }

  .services {
    ${tw`text-white font-display text-center z-10 text-xl leading-normal m:text-3xl m:leading-tight
         mt-4 m:mt-12 l:mt-16 flex flex-col items-center pb-20 m:pb-16 l:pb-24`}

    img {
      ${tw`w-full`}

      max-width: 900px;
    }
  }
`

const Demand360Globe = ({ className, home = false, contactUs = false }) => {
  const data = useStaticQuery(graphql`
    query {
      gcms {
        demand360Globe: contentBlocks(
          where: { identifier: "demand360-globe" }
        ) {
          headerContent {
            html
          }
          mainContent {
            html
          }
          featuredImage {
            url(
              transformation: {
                image: { resize: { width: 1200, fit: clip } }
                document: { output: { format: webp } }
              }
            )
          }
          icon {
            url
          }
          extraData
          callToAction
          linkUrl
        }
        cbContactUsTop: contentBlocks(
          where: { identifier: "demand360-globe-contact-us-top" }
        ) {
          headerContent {
            html
          }
          mainContent {
            html
          }
          featuredImage {
            url(
              transformation: {
                image: { resize: { width: 1200, fit: clip } }
                document: { output: { format: webp } }
              }
            )
          }
          icon {
            url
          }
        }
        cbContactUsBottom: contentBlocks(
          where: { identifier: "demand360-globe-contact-us-bottom" }
        ) {
          headerContent {
            html
          }
          featuredImage {
            url(
              transformation: {
                image: { resize: { width: 1200, fit: clip } }
                document: { output: { format: webp } }
              }
            )
          }
        }
      }
    }
  `)

  const { gcms } = data
  let { demand360Globe, cbContactUsTop, cbContactUsBottom } = gcms
  const { headerContent, mainContent, featuredImage, icon } =
    demand360Globe[0] || {}

  cbContactUsTop = cbContactUsTop[0]
  cbContactUsBottom = cbContactUsBottom[0]

  return (
    <Styles className={className}>
      {!home && (
        <div className="text">
          {!contactUs && (
            <Demand360Logo
              className={className.includes("demand360") ? "demand360" : ""}
            />
          )}
          {!contactUs && headerContent && (
            <div
              className="content"
              dangerouslySetInnerHTML={{
                __html: headerContent.html,
              }}
            />
          )}
          {contactUs && (
            <StyledBlock
              className="text-white secondary-section-header l:mb-12"
              dangerouslySetInnerHTML={{
                __html:
                  cbContactUsTop.headerContent.html +
                  cbContactUsTop.mainContent.html,
              }}
            />
          )}
        </div>
      )}
      <div className="media points">
        {featuredImage && !home && (
          <img className="hero" src={featuredImage.url} />
        )}
        {!contactUs && icon && <img className="data-points" src={icon.url} />}
        {contactUs && (
          <img className="data-points" src={cbContactUsTop.icon.url} />
        )}
        {home && (
          <div className="absolute bottom-6 l:bottom-16 z-10 flex flex-col items-center max-w-s m:max-w-m l:max-w-l">
            <Link to="/products/demand360/">
              <Demand360Logo />
            </Link>
            {mainContent?.html && (
              <div
                className="text mt-8 l:mt-12 text-yellow font-body text-sm m:text-base l:text-xl l:px-40"
                dangerouslySetInnerHTML={{
                  __html: mainContent.html,
                }}
              />
            )}
          </div>
        )}
        <div className="globe">
          <StaticImage
            src="../../images/bg/globe-data-points.jpg"
            alt=""
            placeholder="blurred"
            style={{ position: "absolute", zIndex: -1 }}
            className="top-0 left-0 w-full"
          />
          <video playsInline={true} autoPlay={true} muted={true} loop={true}>
            <source src={GlobeVideo} type="video/mp4" />
          </video>
        </div>
      </div>
      {!(home || contactUs) && (
        <div className="actions">
          <div className="mt-4 l:mt-8">
            <Demand360Features />
          </div>
        </div>
      )}

      {home && <Demand360AvatarCards />}

      {contactUs && (
        <div className="services">
          <div>
            <h5>&nbsp;</h5>
          </div>
          <img alt="" className="" src={cbContactUsBottom.featuredImage.url} />
        </div>
      )}
    </Styles>
  )
}

export default Demand360Globe
